/**
 * /agentic-ops/module/:slug — an Agentic Ops lesson.
 *
 * Unlocked  → learning outcomes + lesson markdown with INLINE per-segment slides
 *             (manifest-driven, like Agentic Mastery) + knowledge-check quiz +
 *             a PDF/PPTX download row.
 * Locked    → preview markdown + an All-Access paywall (OPS.2–5 need all_access).
 * Reuses LessonMarkdown + SectionSlides + useDeckGrant + AgenticQuizBlock.
 */
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  getAgenticOpsModule,
  getAgenticOpsModuleManifest,
  type ModuleManifestSegment,
} from "../api/client";
import LessonMarkdown from "../components/LessonMarkdown";
import SectionSlides from "../components/SectionSlides";
import { useDeckGrant } from "../hooks/useDeckGrant";
import AgenticQuizBlock from "../components/AgenticQuizBlock";

export default function AgenticOpsModulePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-core");
  const { slug = "" } = useParams<{ slug: string }>();

  const {
    data: module,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["agentic-ops-module", slug],
    queryFn: () => getAgenticOpsModule(slug),
    enabled: !!slug,
  });

  // Manifest → inline per-segment slides. 404s (unknown/locked) fall back to a
  // flat lesson render (getAgenticOpsModuleManifest never throws).
  const { data: manifest } = useQuery({
    queryKey: ["agentic-ops-manifest", slug],
    queryFn: () => getAgenticOpsModuleManifest(slug),
    enabled: !!slug,
    retry: false,
  });

  const { manifestByAnchor, practiceAnchors, hasInlineSlides } = useMemo(() => {
    const byAnchor: Record<string, ModuleManifestSegment> = {};
    const anchors: string[] = [];
    let hasSlides = false;
    for (const seg of manifest?.segments ?? []) {
      byAnchor[seg.anchor] = seg;
      if ((seg.slides?.length ?? 0) > 0) {
        anchors.push(seg.anchor);
        hasSlides = true;
      }
    }
    return { manifestByAnchor: byAnchor, practiceAnchors: anchors, hasInlineSlides: hasSlides };
  }, [manifest]);

  // Gated (paid) modules need a deck-access cookie before slide <img>s/downloads
  // fire; free modules serve the deck publicly. Manifest 404s for locked users,
  // so needsGrant only ever applies to unlocked (entitled) users on paid modules.
  const needsGrant = hasInlineSlides && !!module && module.tier !== "free" && !module.locked;
  const { deckReady } = useDeckGrant(module?.slug, { needsGrant });

  const renderSegmentSlides = useCallback(
    (anchor: string) => {
      if (!deckReady) return null;
      const slides = manifestByAnchor[anchor]?.slides ?? [];
      return slides.length ? <SectionSlides slides={slides} /> : null;
    },
    [manifestByAnchor, deckReady],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !module) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{t("AgenticOpsModulePage.errorLoad")}</Alert>
        <Button component={RouterLink} to="/agentic-ops" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          {t("AgenticOpsModulePage.backToOps")}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Button
        component={RouterLink}
        to="/agentic-ops"
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 3 }}
      >
        {t("AgenticOpsModulePage.backButton")}
      </Button>

      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ color: theme.palette.primary.main, fontFamily: "monospace" }}
      >
        {module.number}
      </Typography>
      <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, mb: 1 }}>
        {module.title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        {module.tagline}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
        ~{module.studyTime} · {module.milestone.title}
      </Typography>

      {module.learningOutcomes.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mt: 3,
            mb: 4,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderRadius: 2,
          }}
        >
          <Typography variant="overline" fontWeight={700} sx={{ color: theme.palette.primary.main }}>
            {t("AgenticOpsModulePage.whatYoullLearn")}
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {module.learningOutcomes.map((o, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: theme.palette.primary.main, minWidth: 20 }}
                >
                  {i + 1}.
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.6 }}>
                  {o}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <Divider sx={{ mb: 4 }} />

      {module.locked ? (
        <Box>
          {module.preview && (
            <Box sx={{ mb: 4 }}>
              <LessonMarkdown>{module.preview}</LessonMarkdown>
            </Box>
          )}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              borderRadius: 3,
            }}
          >
            <LockIcon sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.6), mb: 2 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t("AgenticOpsModulePage.paywallTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 460, mx: "auto" }}>
              {t("AgenticOpsModulePage.paywallBody")}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/pricing")}
              endIcon={<ArrowForwardIcon />}
            >
              {t("AgenticOpsModulePage.upgradeButton")}
            </Button>
          </Paper>
        </Box>
      ) : (
        <Box>
          {module.markdown ? (
            <LessonMarkdown
              practiceAnchors={practiceAnchors}
              renderSegmentSlides={renderSegmentSlides}
            >
              {module.markdown}
            </LessonMarkdown>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t("AgenticOpsModulePage.comingSoon")}
            </Alert>
          )}

          <AgenticQuizBlock slug={module.slug} product="agentic-ops" />

          {hasInlineSlides && deckReady && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t("AgenticOpsModulePage.slidesLabel")}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                component="a"
                href={`/decks/${module.slug}/slides/deck.pdf`}
                download
                startIcon={<PictureAsPdfIcon />}
                sx={{ textTransform: "none" }}
              >
                {t("AgenticOpsModulePage.downloadPdf")}
              </Button>
              <Button
                size="small"
                variant="text"
                component="a"
                href={`/decks/${module.slug}/slides/deck.pptx`}
                download
                sx={{ textTransform: "none" }}
              >
                {t("AgenticOpsModulePage.downloadPptx")}
              </Button>
            </Stack>
          )}
        </Box>
      )}

      <Divider sx={{ my: 4 }} />
      <Stack direction="row" justifyContent="space-between">
        {module.prevSlug ? (
          <Button
            component={RouterLink}
            to={`/agentic-ops/module/${module.prevSlug}`}
            startIcon={<ArrowBackIcon />}
          >
            {t("AgenticOpsModulePage.previous")}
          </Button>
        ) : (
          <Box />
        )}
        {module.nextSlug ? (
          <Button
            component={RouterLink}
            to={`/agentic-ops/module/${module.nextSlug}`}
            endIcon={<ArrowForwardIcon />}
          >
            {t("AgenticOpsModulePage.next")}
          </Button>
        ) : (
          <Box />
        )}
      </Stack>
    </Container>
  );
}
